/** Severity auto-suggestion derived from END node data in the Event Sequence Diagram. */

export const SEVERITY_LABELS = ['Minimal', 'Minor', 'Major', 'Hazardous', 'Catastrophic'] as const;

export interface EndNodeSeverityDetail {
    uniqueId: string;
    name: string;
    outcome: string;
    probability: number;
    derivedSeverity: number;
    derivedSeverityLabel: string;
    likelihoodRow: number;
    reason: string;
    isPositive: boolean;
}

/** Likelihood thresholds matching the risk matrix rows (A–G). */
const LIKELIHOOD_THRESHOLDS: Array<{minP: number}> = [
    {minP: 1e-5},   // A — Frequent
    {minP: 1e-7},   // B — Infrequent
    {minP: 1e-8},   // C — Extremely Infrequent
    {minP: 1e-9},   // D — Remote
    {minP: 1e-10},  // E — Extremely Remote
    {minP: 1e-11},  // F — Improbable
    {minP: 0},      // G — Extremely Improbable
];

function getLikelihoodRow(p: number): number {
    for (let i = 0; i < LIKELIHOOD_THRESHOLDS.length; i++) {
        if (p >= LIKELIHOOD_THRESHOLDS[i].minP) return i;
    }
    return LIKELIHOOD_THRESHOLDS.length - 1;
}

export interface SeveritySuggestion {
    suggestedSeverity: number;
    suggestedLikelihood: number;
    weightedAverage: number;
    weightedLikelihood: number;
    endNodeDetails: EndNodeSeverityDetail[];
}

// Keyword patterns ordered from most severe to least.
// Each entry: [severity index, regex pattern, reason label]
const CATASTROPHIC_PATTERNS: RegExp[] = [
    /\bfatalit/i, /\bfatal\b/i, /\bdeath/i, /\bcollision\b/i,
    /\bcrash/i, /\bcfit\b/i, /\bmid[- ]air\b/i, /\bcollides\b/i,
];

const HAZARDOUS_PATTERNS: RegExp[] = [
    /\bhull loss\b/i, /\bserious injur/i, /\bditching\b/i,
    /\bmultiple injur/i,
];

const MAJOR_PATTERNS: RegExp[] = [
    /\binjur/i, /\bsubstantial damage\b/i,
];

const MINOR_PATTERNS: RegExp[] = [
    /\bdamage\b/i, /\bmaintenance inspection\b/i,
];

const MINIMAL_PATTERNS: RegExp[] = [
    /\bwithout further incident\b/i, /\bdid not result in\b/i,
    /\bwithout other aircraft\b/i, /\bwithout other vehicle\b/i,
    /\bno damage\b/i, /\bno injur/i, /\bnegligible\b/i,
    /\bnormal operation/i,
];

interface SeverityMatch {
    severity: number;
    reason: string;
}

function matchKeywords(text: string, patterns: RegExp[], severity: number, label: string): SeverityMatch | null {
    for (const p of patterns) {
        const match = text.match(p);
        if (match) return {severity, reason: `Keyword: "${match[0]}" → ${label}`};
    }
    return null;
}

function deriveSeverityFromText(text: string): SeverityMatch | null {
    return matchKeywords(text, CATASTROPHIC_PATTERNS, 4, 'Catastrophic')
        ?? matchKeywords(text, HAZARDOUS_PATTERNS, 3, 'Hazardous')
        ?? matchKeywords(text, MAJOR_PATTERNS, 2, 'Major')
        ?? matchKeywords(text, MINOR_PATTERNS, 1, 'Minor')
        ?? matchKeywords(text, MINIMAL_PATTERNS, 0, 'Minimal')
        ?? null;
}

interface EndNodeInput {
    uniqueId?: string;
    name?: string;
    description?: string;
    type?: string;
    outcome?: string;
    probability?: number;
    frequency?: number;
    accidentFatalityProbability?: number;
}

function deriveSeverityFromEndNode(node: EndNodeInput): {severity: number; reason: string} | null {
    const outcome = node.outcome ?? '';

    // POSITIVE outcomes are safe — Minimal severity
    if (outcome === 'POSITIVE') return {severity: 0, reason: 'Positive outcome → Minimal'};

    // accidentFatalityProbability override (when provided)
    const afp = node.accidentFatalityProbability ?? 0;
    if (afp > 0) {
        if (afp >= 0.5) return {severity: 4, reason: `Fatality probability ${(afp * 100).toFixed(0)}% → Catastrophic`};
        if (afp >= 0.1) return {severity: 3, reason: `Fatality probability ${(afp * 100).toFixed(0)}% → Hazardous`};
        if (afp >= 0.01) return {severity: 2, reason: `Fatality probability ${(afp * 100).toFixed(1)}% → Major`};
        return {severity: 1, reason: `Fatality probability ${(afp * 100).toFixed(2)}% → Minor`};
    }

    // Combine name and description for keyword matching
    const text = [node.name ?? '', node.description ?? ''].join(' ');

    if (outcome === 'NEGATIVE') {
        // NEGATIVE outcome → at least Hazardous
        const textMatch = deriveSeverityFromText(text);
        if (textMatch && textMatch.severity >= 3) return textMatch;
        // Default for NEGATIVE with no catastrophic keywords
        return {severity: 3, reason: 'Negative outcome → Hazardous (default)'};
    }

    // NEUTRAL outcome → Minimal to Major range
    const textMatch = deriveSeverityFromText(text);
    if (textMatch) return textMatch;

    // Default for NEUTRAL with no keyword match
    return {severity: 0, reason: 'Neutral outcome → Minimal (default)'};
}

export function computeSeveritySuggestion(events: EndNodeInput[]): SeveritySuggestion | null {
    const endNodes = events.filter((e) => e.type === 'END');
    if (endNodes.length === 0) return null;

    const details: EndNodeSeverityDetail[] = [];
    let weightedSeveritySum = 0;
    let weightedLikelihoodSum = 0;
    let totalWeight = 0;

    for (const node of endNodes) {
        const result = deriveSeverityFromEndNode(node);
        if (!result) continue;

        const positive = node.outcome === 'POSITIVE';
        const prob = (node.probability ?? 0) || ((node.frequency ?? 0) * 1e-6);
        details.push({
            uniqueId: node.uniqueId ?? '',
            name: node.name ?? 'Unknown',
            outcome: node.outcome ?? 'Unknown',
            probability: prob,
            derivedSeverity: result.severity,
            derivedSeverityLabel: SEVERITY_LABELS[result.severity],
            likelihoodRow: getLikelihoodRow(prob),
            reason: result.reason,
            isPositive: positive,
        });

        // POSITIVE nodes are excluded from the weighted averages
        if (!positive) {
            const likelihoodRow = getLikelihoodRow(prob);
            weightedSeveritySum += result.severity * prob;
            weightedLikelihoodSum += likelihoodRow * prob;
            totalWeight += prob;
        }
    }

    if (details.length === 0) return null;

    const nonPositive = details.filter((d) => !d.isPositive);

    // If all probabilities are 0, fall back to simple average (excluding POSITIVE)
    let weightedAverage: number;
    let weightedLikelihood: number;
    if (totalWeight > 0) {
        weightedAverage = weightedSeveritySum / totalWeight;
        weightedLikelihood = weightedLikelihoodSum / totalWeight;
    } else if (nonPositive.length > 0) {
        weightedAverage = nonPositive.reduce((sum, d) => sum + d.derivedSeverity, 0) / nonPositive.length;
        weightedLikelihood = nonPositive.reduce((sum, d) => sum + d.likelihoodRow, 0) / nonPositive.length;
    } else {
        weightedAverage = 0; // All END nodes are POSITIVE
        weightedLikelihood = LIKELIHOOD_THRESHOLDS.length - 1;
    }

    const suggestedSeverity = Math.round(weightedAverage);
    const suggestedLikelihood = Math.round(weightedLikelihood);

    return {
        suggestedSeverity: Math.max(0, Math.min(4, suggestedSeverity)),
        suggestedLikelihood: Math.max(0, Math.min(LIKELIHOOD_THRESHOLDS.length - 1, suggestedLikelihood)),
        weightedAverage,
        weightedLikelihood,
        endNodeDetails: details,
    };
}
